/**
 * mul-div-gen — 康軒數學三下「第 7 單元 乘法與除法」考卷產生器。
 * 對照 `math-exam-gen.ts` / `stats-table-gen.ts` / `time-exam-gen.ts` 同一套 lockdown 架構
 *(**seeded RNG + 策展閉集 / 受控變數 + 程式可驗證答案 + validator + 200-seed regression**)。
 *
 * grounded 在康軒三下第 7 章課本真實內容(boss 拍攝 p83–102),五個題型區塊:
 *   ① inverse 乘除互逆(一組四算式)—— 給一個乘法事實(a×b)或除法事實((a×b)÷a),
 *      補出同一組另外 3 個算式:a×b、b×a、(a×b)÷a=b、(a×b)÷b=a。
 *      乘法方向可含 3 位數 × 1 位數(89×3=267,作為 267÷3 的逆),**不出多位數×多位數
 *      乘法直式(mulvert,三上)**。課本例:7×4=28 → 4×7、28÷4、28÷7;56÷7=8 → 56÷8、7×8、8×7。
 *   ② unknown 未知數三型(課本活動 2 / 活動 3 主軸)——
 *      ・被乘數 / 乘數未知:`( )×5=100`、`3×( )=36` → 答 = 積 ÷ 已知因數(20、12)。
 *      ・被除數未知:`( )÷4=12` → 答 = 商 × 除數(48)。
 *      ・除數未知:`91÷( )=7`、`160÷( )=8` → 答 = 被除數 ÷ 商(13、20)。
 *      recompute:把求出的數代回原式必成立(唯一真相)。
 *   ③ multiples 倍數(×N / ÷N 倍器 + 數線 + round-trip)—— 輸入 × N → 輸出;反向 ÷N;
 *      數線「起點 0、一次跳 N 格、跳 k 次 = N×k」。例:5×2=10、24÷4=6、3×4=12(數線)。
 *   ④ longdiv 直式除法 + 驗算(含餘數)—— 兩三位數 ÷ 一位數(被除數 100..999、除數 2..9),
 *      商可能含 0、可能有餘數。**驗算含餘數**:商×除數(+餘數)=被除數(82÷7=11…5 →
 *      11×7+5=82)。有餘數型佔一定比例。
 *   ⑤ word 乘除應用題(情境)—— 平分 / 倍數比例 / 等分串。用課本詞彙(餅乾 / 布丁 /
 *      牛奶 mL / 棉花糖),數值整除或明確餘數。
 *
 * 鐵則 / 設計(對齊 feedback_generator_source_lockdown,不打地鼠):
 *   - **seeded RNG**(別用 Math.random):同 seed → 同卷,可重現;UI「重抽」= 換 seed。
 *   - **答案程式可驗證**:每題 answer 由 `recomputeMulDivAnswer(verify)` 反算,validator 重算後比對。
 *     乘除 / 餘數 / 驗算式 / 未知數代回都由 recompute 算,不另寫一份答案。
 *   - **不超綱**:乘法只到「九九 + 3 位數 × 1 位數(逆向用,不出直式)」;除法被除數 ≤ 999、
 *     除數一位數;**不得出乘法直式(mulvert,三上)**。
 *   - **數值鎖在三下合理範圍**:因數 / 積 / 商 / 餘數非負整數、餘數 < 除數、被乘數×乘數 ≤ 999。
 *   - **lockdown 防壞題**:模板 = 策展閉集常數;硬規則寫進產生器 + validator;200-seed regression 全過。
 *
 * 學習/遊戲分離:純學習側,**絕不**碰 commanderXp / 戰鬥數值。
 */

// ───────────────────────────── 型別 ─────────────────────────────

/** 題型(五種)。 */
export type MulDivQuestionKind = 'inverse' | 'unknown' | 'multiples' | 'longdiv' | 'word';

/**
 * 一題的反算規格(closed set)。validator / 線上批改依 `op` 重算 → 與 `answer` 比對。
 *   - 'inverse-group':給一組事實的 a,b(積 = a×b),補出**另外 3 個算式**(多行字串)。
 *   - 'unknown-factor':( )×known=product 或 known×( )=product → 答 = product ÷ known。
 *   - 'unknown-dividend':( )÷divisor=quotient → 答 = quotient × divisor。
 *   - 'unknown-divisor':dividend÷( )=quotient → 答 = dividend ÷ quotient。
 *   - 'times-multiply':input × n → 答 = input×n(×N 倍器 / 數線跳格)。
 *   - 'times-divide':value ÷ n → 答 = value÷n(÷N 反向倍器)。
 *   - 'longdiv':dividend ÷ divisor → 答 = '商 X 餘 Y'(Y=0 時 '商 X 整除')。
 *   - 'longdiv-check':dividend ÷ divisor → 答 = 驗算式 '商×除數(+餘)=被除數'。
 *   - 'word-divide':total ÷ groups → 答 = quotient + unit(平分,整除)。
 *   - 'word-ratio':base 量對 baseQty,問 askQty 對多少 → 答 = baseQty / base × askQty + unit。
 *   - 'word-remainder':total ÷ per → 答 = '商…餘'(明確餘數情境)。
 */
export type MulDivVerify =
  | { op: 'inverse-group'; a: number; b: number }
  | { op: 'unknown-factor'; known: number; product: number; knownIsFirst: boolean }
  | { op: 'unknown-dividend'; divisor: number; quotient: number }
  | { op: 'unknown-divisor'; dividend: number; quotient: number }
  | { op: 'times-multiply'; input: number; n: number }
  | { op: 'times-divide'; value: number; n: number }
  | { op: 'longdiv'; dividend: number; divisor: number }
  | { op: 'longdiv-check'; dividend: number; divisor: number }
  | { op: 'word-divide'; total: number; groups: number; unit: string }
  | { op: 'word-ratio'; base: number; baseQty: number; ask: number; unit: string }
  | { op: 'word-remainder'; total: number; per: number; unit: string };

/** 一道乘除題:題幹 + 程式可驗證答案 + 反算用 verify。 */
export interface MulDivQuestion {
  id: string;
  /** 題幹(顯示 / 列印用)。 */
  prompt: string;
  /** 正解人類可讀字串(顯示 / 批改用)。 */
  answer: string;
  /** 反算規格(validator / 批改重算用)。 */
  verify: MulDivVerify;
}

/** 一大題 = 一種題型 + 題目陣列 + 標籤 / 標題 / 配分。 */
export interface MulDivSection {
  kind: MulDivQuestionKind;
  /** 大題標籤,例 '一'。 */
  label: string;
  /** 大題標題,例 '乘除互逆'。 */
  title: string;
  /** 作答指示語。 */
  instructions: string;
  /** 每題配分。 */
  pointsEach: number;
  /** 該大題的題目。 */
  questions: MulDivQuestion[];
}

/** 一份乘除考卷。 */
export interface MulDivExam {
  id: string;
  title: string;
  /** 教科書版本(數學 = 康軒)。 */
  textbook: '康軒';
  /** 範圍標註(顯示用)。 */
  scope: string;
  sections: MulDivSection[];
}

/** 各題型題數(預設見 DEFAULT_COUNTS;key = MulDivQuestionKind)。 */
export type MulDivCounts = Partial<Record<MulDivQuestionKind, number>>;

/** `generateMulDivExam` 選項。 */
export interface MulDivGenOpts {
  /** 可重現種子(同 seed 同卷);UI「重抽」= 換 seed。 */
  seed: number;
  /** 各題型題數,預設見 DEFAULT_COUNTS。 */
  counts?: MulDivCounts;
  /** 卷 id(預設 `mul-div-<seed>`)。 */
  id?: string;
  /** 卷名(預設 `乘法與除法考卷 · 第 <seed> 回`)。 */
  title?: string;
}

// ───────────────────────────── seeded RNG ─────────────────────────────

/**
 * 確定性 RNG —— mulberry32(與 math-exam-gen / stats-table-gen / time-exam-gen 同款,
 * 純函式可重現)。注入 seed,杜絕 Math.random。
 */
export function makeRng(seed: number): () => number {
  let a = seed >>> 0;
  return function next(): number {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** [min, max] 的整數(含兩端)。 */
function randInt(rng: () => number, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min;
}

/** 抽一個元素。 */
function pick<T>(rng: () => number, arr: readonly T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

// ───────────────────────────── 受控閉集 ─────────────────────────────

/** 倍數倍器候選 N(課本 ×2/×3/×4/×5/×7…)。 */
const MULTIPLIER_OPTIONS = [2, 3, 4, 5, 6, 7, 8, 9] as const;

/** 平分應用題情境(整除):total 個 ÷ groups → 每份。物品用課本詞彙。 */
interface DivideScene {
  /** 物品(餅乾 / 棉花糖 …)。 */
  item: string;
  /** 分配對象量詞(班 / 盤 / 袋 …)。 */
  groupWord: string;
  /** 每份量詞(個 / 顆 …)。 */
  perUnit: string;
}

const DIVIDE_SCENES: readonly DivideScene[] = [
  { item: '餅乾', groupWord: '盤', perUnit: '片' },
  { item: '棉花糖', groupWord: '袋', perUnit: '顆' },
  { item: '貼紙', groupWord: '本', perUnit: '張' },
  { item: '糖果', groupWord: '盒', perUnit: '顆' },
];

/** 倍數 / 比例應用題情境:base 單位對 baseQty(如 5 個布丁要 500 mL 牛奶)。 */
interface RatioScene {
  /** 主物品(布丁 …)。 */
  item: string;
  /** 主物品量詞(個 …)。 */
  itemUnit: string;
  /** 配料名(牛奶 …)。 */
  resource: string;
  /** 配料單位(mL …)。 */
  resourceUnit: string;
  /** 基準:base 個主物品需 perBase 單位配料(perBase 為每「base 個」的量)。 */
  base: number;
  perBase: number;
}

const RATIO_SCENES: readonly RatioScene[] = [
  { item: '布丁', itemUnit: '個', resource: '牛奶', resourceUnit: 'mL', base: 5, perBase: 500 },
  { item: '鬆餅', itemUnit: '片', resource: '麵粉', resourceUnit: '公克', base: 4, perBase: 200 },
  { item: '果凍', itemUnit: '個', resource: '果汁', resourceUnit: 'mL', base: 3, perBase: 150 },
];

/** 有餘數應用題情境:total 個,每 per 個一份,問做幾份餘幾個。 */
interface RemainderScene {
  item: string;
  perUnit: string;
  /** 一份的單位(串 / 包 …)。 */
  bundleWord: string;
}

const REMAINDER_SCENES: readonly RemainderScene[] = [
  { item: '棉花糖', perUnit: '顆', bundleWord: '串' },
  { item: '餅乾', perUnit: '片', bundleWord: '包' },
  { item: '彈珠', perUnit: '顆', bundleWord: '袋' },
];

// ───────────────────────────── 反算核心(唯一真相)─────────────────────────────

/**
 * 乘除互逆「另外的算式」字串(多行)。給 a×b 這組事實(**invariant:a 為一位因數 ≤ 9**):
 *   - 兩因數皆一位(b ≤ 9)→ 補 b×a=積、積÷a=b、積÷b=a(完整四算式組,3 行;兩個除法
 *     的除數都是一位數,合三下範圍)。
 *   - b 為多位(3 位數 × 1 位數的逆向,如 89×3=267)→ 只補 b×a=積、積÷a=b(2 行);
 *     **不出「積÷b」**(那會變成「除以多位數」,超綱;課本只教 267÷3 這個 1 位除數方向)。
 * recompute 唯一真相:除數一律 ≤ 9。
 */
function inverseGroupLines(a: number, b: number): string {
  const p = a * b;
  // a 恆為一位因數;b ≤ 9 時可再出「積÷b=a」(除數一位數),b 多位時略去(不除以多位數)。
  const lines = [`${b}×${a}=${p}`, `${p}÷${a}=${b}`];
  if (b <= 9) lines.push(`${p}÷${b}=${a}`);
  // a===b(如 16=4×4)時 `積÷a` 與 `積÷b` 相同 → 去重(避免重複算式)。
  return [...new Set(lines)].join('\n');
}

/**
 * 依一題的 `verify` 規格,重算期望答案字串。validator 與線上批改共用此函式(單一真相)。
 * 規格不合法(非整數 / 越界 / 不整除…)回 null(視為壞題)。
 */
export function recomputeMulDivAnswer(v: MulDivVerify): string | null {
  switch (v.op) {
    case 'inverse-group': {
      if (!Number.isInteger(v.a) || !Number.isInteger(v.b)) return null;
      if (v.a < 2 || v.b < 2) return null;
      if (v.a > 9) return null; // a 必為一位因數(除數一律 ≤ 9,不出除以多位數)
      if (v.a * v.b > 999) return null; // 不超綱(積 ≤ 999)
      return inverseGroupLines(v.a, v.b);
    }
    case 'unknown-factor': {
      if (!Number.isInteger(v.known) || !Number.isInteger(v.product)) return null;
      if (v.known < 1 || v.product < 1) return null;
      if (v.product % v.known !== 0) return null; // 必整除(答為整數)
      return String(v.product / v.known);
    }
    case 'unknown-dividend': {
      if (!Number.isInteger(v.divisor) || !Number.isInteger(v.quotient)) return null;
      if (v.divisor < 1 || v.quotient < 1) return null;
      return String(v.quotient * v.divisor);
    }
    case 'unknown-divisor': {
      if (!Number.isInteger(v.dividend) || !Number.isInteger(v.quotient)) return null;
      if (v.dividend < 1 || v.quotient < 1) return null;
      if (v.dividend % v.quotient !== 0) return null; // 必整除
      return String(v.dividend / v.quotient);
    }
    case 'times-multiply': {
      if (!Number.isInteger(v.input) || !Number.isInteger(v.n)) return null;
      if (v.input < 0 || v.n < 1) return null;
      return String(v.input * v.n);
    }
    case 'times-divide': {
      if (!Number.isInteger(v.value) || !Number.isInteger(v.n)) return null;
      if (v.value < 0 || v.n < 1) return null;
      if (v.value % v.n !== 0) return null; // 反向倍器必整除
      return String(v.value / v.n);
    }
    case 'longdiv': {
      if (!Number.isInteger(v.dividend) || !Number.isInteger(v.divisor)) return null;
      if (v.dividend < 0 || v.divisor < 2 || v.divisor > 9) return null;
      const q = Math.floor(v.dividend / v.divisor);
      const r = v.dividend % v.divisor;
      return r === 0 ? `商 ${q} 整除` : `商 ${q} 餘 ${r}`;
    }
    case 'longdiv-check': {
      if (!Number.isInteger(v.dividend) || !Number.isInteger(v.divisor)) return null;
      if (v.dividend < 0 || v.divisor < 2 || v.divisor > 9) return null;
      const q = Math.floor(v.dividend / v.divisor);
      const r = v.dividend % v.divisor;
      // 驗算式:商×除數(+餘數)=被除數。
      return r === 0
        ? `${q}×${v.divisor}=${v.dividend}`
        : `${q}×${v.divisor}+${r}=${v.dividend}`;
    }
    case 'word-divide': {
      if (!Number.isInteger(v.total) || !Number.isInteger(v.groups)) return null;
      if (v.total < 1 || v.groups < 1) return null;
      if (v.total % v.groups !== 0) return null; // 平分必整除
      return `${v.total / v.groups} ${v.unit}`;
    }
    case 'word-ratio': {
      if (!Number.isInteger(v.base) || !Number.isInteger(v.baseQty) || !Number.isInteger(v.ask)) {
        return null;
      }
      if (v.base < 1 || v.baseQty < 1 || v.ask < 1) return null;
      if (v.ask % v.base !== 0) return null; // ask 必為 base 的整數倍(比例可整除)
      const perOne = v.baseQty / v.base;
      if (!Number.isInteger(perOne)) return null;
      return `${perOne * v.ask} ${v.unit}`;
    }
    case 'word-remainder': {
      if (!Number.isInteger(v.total) || !Number.isInteger(v.per)) return null;
      if (v.total < 1 || v.per < 1) return null;
      const q = Math.floor(v.total / v.per);
      const r = v.total % v.per;
      return `${q} ${v.unit} 餘 ${r}`;
    }
  }
}

// ───────────────────────────── 各題型生成(從規格反算 answer)─────────────────────────────

/**
 * ① inverse 乘除互逆(一組四算式)。給一個乘法或除法事實,要學生補另外 3 個算式。
 * 因數 a,b:偶數題用九九範圍(2..9 × 2..9);奇數題乘法方向含 3 位數 × 1 位數
 *(a ∈ 2..9、b 為 2 / 3 位數使積 ≤ 999,作為「積÷a / 積÷b」的逆向),仍不出乘法直式。
 */
function genInverseQuestions(rng: () => number, n: number): MulDivQuestion[] {
  const out: MulDivQuestion[] = [];
  for (let i = 0; i < n; i++) {
    let a: number;
    let b: number;
    if (i % 2 === 0) {
      // 九九事實(a≠b,避免 a×a 退化成重複算式)。a 恆為一位因數(除數方向用)。
      a = randInt(rng, 2, 9);
      do {
        b = randInt(rng, 2, 9);
      } while (b === a);
    } else {
      // 3 位數 × 1 位數方向(逆向用,積 ≤ 999):a 為一位因數(逆向除數),b 為較大因數。
      a = randInt(rng, 2, 9);
      b = randInt(rng, 11, Math.floor(999 / a));
    }
    const p = a * b;
    // 大因數 b(多位)時逆向只有「÷a」一個除法方向,故補 2 個算式;九九則補 3 個。
    const countWord = b <= 9 ? '3' : '2';
    // 題幹給乘法事實或除法事實(交替;除法事實一律以一位除數 a 呈現,合三下範圍)。
    const giveDivision = i % 4 >= 2;
    const prompt = giveDivision
      ? `下面是同一組的算式。已知「${p}÷${a}=${b}」,請寫出同一組的另外 ${countWord} 個算式(每行一個)。`
      : `下面是同一組的算式。已知「${a}×${b}=${p}」,請寫出同一組的另外 ${countWord} 個算式(每行一個)。`;
    out.push({
      id: `iv${i + 1}`,
      prompt,
      answer: inverseGroupLines(a, b),
      verify: { op: 'inverse-group', a, b },
    });
  }
  return out;
}

/**
 * ② unknown 未知數三型(課本活動 2 / 3 主軸)。
 * variant 0/1 = 因數未知(被乘數 / 乘數)、variant 2 = 被除數未知、variant 3 = 除數未知。
 * 數值受控:因數 ∈ 2..9、積 ≤ 999、商 ∈ 2..20、被除數整除。
 */
function genUnknownQuestions(rng: () => number, n: number): MulDivQuestion[] {
  const out: MulDivQuestion[] = [];
  for (let i = 0; i < n; i++) {
    const variant = i % 4;
    if (variant === 0 || variant === 1) {
      // 因數未知:( )×known=product 或 known×( )=product。
      const known = randInt(rng, 2, 9);
      const other = randInt(rng, 2, 30);
      const product = known * other;
      const knownIsFirst = variant === 1; // known×( ) 形
      const prompt = knownIsFirst
        ? `算式裡的( )是多少?${known}×( )=${product}`
        : `算式裡的( )是多少?( )×${known}=${product}`;
      out.push({
        id: `uk${i + 1}`,
        prompt,
        answer: String(other),
        verify: { op: 'unknown-factor', known, product, knownIsFirst },
      });
    } else if (variant === 2) {
      // 被除數未知:( )÷divisor=quotient → 被除數 = quotient × divisor。
      const divisor = randInt(rng, 2, 9);
      const quotient = randInt(rng, 2, 20);
      out.push({
        id: `uk${i + 1}`,
        prompt: `算式裡的( )是多少?( )÷${divisor}=${quotient}`,
        answer: String(quotient * divisor),
        verify: { op: 'unknown-dividend', divisor, quotient },
      });
    } else {
      // 除數未知:dividend÷( )=quotient → 除數 = dividend ÷ quotient。
      const quotient = randInt(rng, 2, 9);
      const divisor = randInt(rng, 2, 12);
      const dividend = quotient * divisor;
      out.push({
        id: `uk${i + 1}`,
        prompt: `算式裡的( )是多少?${dividend}÷( )=${quotient}`,
        answer: String(divisor),
        verify: { op: 'unknown-divisor', dividend, quotient },
      });
    }
  }
  return out;
}

/**
 * ③ multiples 倍數(×N / ÷N 倍器 + 數線 + round-trip)。
 * variant 0 = ×N 倍器;variant 1 = ÷N 反向倍器;variant 2 = 數線跳格(0 起跳 N 格 k 次 = N×k)。
 */
function genMultiplesQuestions(rng: () => number, n: number): MulDivQuestion[] {
  const out: MulDivQuestion[] = [];
  for (let i = 0; i < n; i++) {
    const variant = i % 3;
    if (variant === 0) {
      // ×N 倍器:input × N。
      const nn = pick(rng, MULTIPLIER_OPTIONS);
      const input = randInt(rng, 2, Math.floor(99 / nn));
      out.push({
        id: `mp${i + 1}`,
        prompt: `把 ${input} 放進「×${nn} 倍器」,會跑出多少?`,
        answer: String(input * nn),
        verify: { op: 'times-multiply', input, n: nn },
      });
    } else if (variant === 1) {
      // ÷N 反向倍器:value ÷ N(value 為 N 的倍數)。
      const nn = pick(rng, MULTIPLIER_OPTIONS);
      const q = randInt(rng, 2, Math.floor(99 / nn));
      const value = q * nn;
      out.push({
        id: `mp${i + 1}`,
        prompt: `把 ${value} 放進「÷${nn} 倍器」,會跑出多少?`,
        answer: String(q),
        verify: { op: 'times-divide', value, n: nn },
      });
    } else {
      // 數線跳格:0 起跳,一次跳 N 格,跳 k 次到哪裡 = N×k。
      const nn = pick(rng, MULTIPLIER_OPTIONS);
      const k = randInt(rng, 2, Math.min(9, Math.floor(99 / nn)));
      out.push({
        id: `mp${i + 1}`,
        prompt: `在數線上從 0 出發,一次跳 ${nn} 格,跳 ${k} 次會停在哪個數?`,
        answer: String(nn * k),
        verify: { op: 'times-multiply', input: k, n: nn },
      });
    }
  }
  return out;
}

/**
 * ④ longdiv 直式除法 + 驗算(含餘數)。被除數 100..999、除數 2..9,商可能含 0、可能有餘數。
 * 求商題(longdiv)與驗算題(longdiv-check)交替(偶 / 奇位),確保兩型都出。
 * **有餘數獨立於題型**(boss:驗算必須含餘數,有餘數型佔比足):前 2 題鎖定有餘數(其中含
 * 一題驗算題,保證「含餘數的驗算式」一定出現),其餘約 60% 機率有餘數,其餘整除。
 */
function genLongDivQuestions(rng: () => number, n: number): MulDivQuestion[] {
  const out: MulDivQuestion[] = [];
  for (let i = 0; i < n; i++) {
    const divisor = randInt(rng, 2, 9);
    // 有餘數與否「不」綁題型 parity:前 2 題強制有餘數(i=0 求商、i=1 驗算,皆含餘數),
    // 其餘約 60% 有餘數,確保「求商有餘」「驗算含餘」兩種都穩定出現。
    const forceRemainder = i < 2;
    const wantRemainder = forceRemainder || rng() < 0.6;
    const q = randInt(rng, Math.ceil(100 / divisor), Math.floor(999 / divisor));
    let dividend: number;
    if (wantRemainder) {
      const r = randInt(rng, 1, divisor - 1);
      dividend = q * divisor + r;
      if (dividend > 999) dividend -= divisor; // 收回 ≤ 999(仍保有餘數,仍 ≥ 100)
    } else {
      dividend = q * divisor; // 整除
    }
    // 求商題在偶數位、驗算題在奇數位,確保兩型都出(與有餘數獨立)。
    if (i % 2 === 0) {
      out.push({
        id: `ld${i + 1}`,
        prompt: `用直式算 ${dividend}÷${divisor},商是多少?有餘數要寫出來(沒有餘數寫「整除」)。`,
        answer: recomputeMulDivAnswer({ op: 'longdiv', dividend, divisor })!,
        verify: { op: 'longdiv', dividend, divisor },
      });
    } else {
      out.push({
        id: `ld${i + 1}`,
        prompt: `算出 ${dividend}÷${divisor} 後,寫出驗算式(商×除數+餘數=被除數)。`,
        answer: recomputeMulDivAnswer({ op: 'longdiv-check', dividend, divisor })!,
        verify: { op: 'longdiv-check', dividend, divisor },
      });
    }
  }
  return out;
}

/**
 * ⑤ word 乘除應用題(情境)。variant 0 = 平分(整除)、variant 1 = 倍數 / 比例、
 * variant 2 = 有餘數情境。情境用課本詞彙,數值整除或明確餘數。
 */
function genWordQuestions(rng: () => number, n: number): MulDivQuestion[] {
  const out: MulDivQuestion[] = [];
  for (let i = 0; i < n; i++) {
    const variant = i % 3;
    if (variant === 0) {
      // 平分(整除):total 個物品平分給 groups 份,每份幾個。
      const sc = pick(rng, DIVIDE_SCENES);
      const groups = randInt(rng, 3, 9);
      const per = randInt(rng, 6, 20);
      const total = groups * per; // 保證整除
      out.push({
        id: `wd${i + 1}`,
        prompt: `有 ${total} ${sc.perUnit}${sc.item},平分成 ${groups} ${sc.groupWord},每${sc.groupWord}有幾${sc.perUnit}?`,
        answer: `${per} ${sc.perUnit}`,
        verify: { op: 'word-divide', total, groups, unit: sc.perUnit },
      });
    } else if (variant === 1) {
      // 倍數 / 比例:base 個主物品要 perBase 配料,問 ask 個要多少配料。
      const sc = pick(rng, RATIO_SCENES);
      const k = randInt(rng, 2, 5); // ask = base × k(整數倍,可整除)
      const ask = sc.base * k;
      const total = sc.perBase * k;
      out.push({
        id: `wd${i + 1}`,
        prompt: `做 ${sc.base} ${sc.itemUnit}${sc.item}要用 ${sc.perBase} ${sc.resourceUnit}${sc.resource},做 ${ask} ${sc.itemUnit}要用多少 ${sc.resourceUnit}${sc.resource}?`,
        answer: `${total} ${sc.resourceUnit}`,
        verify: { op: 'word-ratio', base: sc.base, baseQty: sc.perBase, ask, unit: sc.resourceUnit },
      });
    } else {
      // 有餘數情境:total 個,每 per 個串 1 串,可做幾串餘幾個。
      const sc = pick(rng, REMAINDER_SCENES);
      const per = randInt(rng, 4, 9);
      const bundles = randInt(rng, 5, 20);
      const rem = randInt(rng, 1, per - 1);
      const total = bundles * per + rem; // 保證有餘數
      out.push({
        id: `wd${i + 1}`,
        prompt: `有 ${total} ${sc.perUnit}${sc.item},每 ${per} ${sc.perUnit}串成 1 ${sc.bundleWord},可以串幾${sc.bundleWord}?還剩幾${sc.perUnit}?`,
        answer: `${bundles} ${sc.bundleWord} 餘 ${rem}`,
        verify: { op: 'word-remainder', total, per, unit: sc.bundleWord },
      });
    }
  }
  return out;
}

// ───────────────────────────── 組卷 ─────────────────────────────

/**
 * 各題型題數(預設)。五大題(grounded 在課本第 7 章),Σ(pointsEach×count) = 100:
 *   inverse 4×5=20(乘除互逆四算式)/ unknown 4×5=20(未知數三型)/
 *   multiples 4×4=16(×N/÷N/數線)/ longdiv 4×6=24(直式除法含餘數+驗算)/
 *   word 4×5=20 → 100。涵蓋課本第 7 章五型 + 各型多題種。
 */
const DEFAULT_COUNTS: Required<MulDivCounts> = {
  inverse: 5,
  unknown: 5,
  multiples: 4,
  longdiv: 6,
  word: 5,
};

/** 固定出題順序(由淺入深):乘除互逆 → 未知數三型 → 倍數 → 直式除法 → 應用題。 */
const SECTION_ORDER: MulDivQuestionKind[] = ['inverse', 'unknown', 'multiples', 'longdiv', 'word'];

const SECTION_META: Record<
  MulDivQuestionKind,
  { title: string; instructions: string; pointsEach: number }
> = {
  inverse: {
    title: '乘除互逆(一組四算式)',
    instructions:
      '同一組的乘法和除法會用到一樣的三個數。看題目給的算式,寫出同一組的另外 3 個算式(每行寫一個)。每題 4 分。',
    pointsEach: 4,
  },
  unknown: {
    title: '找出算式裡的( )',
    instructions:
      '用乘除互逆的關係算出( )。因數未知用除的,被除數未知用乘的,除數未知用被除數除以商。每題 4 分。',
    pointsEach: 4,
  },
  multiples: {
    title: '倍數(倍器與數線)',
    instructions:
      '「×N 倍器」把數變大 N 倍、「÷N 倍器」是反過來。數線從 0 一次跳 N 格,跳 k 次就是 N×k。每題 4 分。',
    pointsEach: 4,
  },
  longdiv: {
    title: '直式除法與驗算',
    instructions:
      '用直式算除法,有餘數要寫出餘數。驗算的方法:商×除數+餘數=被除數(沒有餘數就不加餘數)。每題 4 分。',
    pointsEach: 4,
  },
  word: {
    title: '乘除應用題',
    instructions:
      '先想清楚是「平分(用除)」「倍數(用乘)」還是「有剩下(用除看餘數)」,列式再算,記得寫單位。每題 4 分。',
    pointsEach: 4,
  },
};

const CN_SECTION_LABELS = ['一', '二', '三', '四', '五', '六'];

/**
 * 從受控閉集生一份乘除考卷。同 seed → 同卷(可重現)。
 *
 * @param opts seed(必填)+ 可選題數 / id / title。
 * @returns 一份 `MulDivExam`(預設五大題:互逆 / 未知數 / 倍數 / 直式除法 / 應用題)。
 *   生成後建議過 `validateMulDivExam`。
 */
export function generateMulDivExam(opts: MulDivGenOpts): MulDivExam {
  const rng = makeRng(opts.seed);
  const counts: Required<MulDivCounts> = { ...DEFAULT_COUNTS, ...opts.counts };

  const sections: MulDivSection[] = SECTION_ORDER.map((kind, idx) => {
    const meta = SECTION_META[kind];
    const nn = counts[kind];
    let questions: MulDivQuestion[];
    if (kind === 'inverse') {
      questions = genInverseQuestions(rng, nn);
    } else if (kind === 'unknown') {
      questions = genUnknownQuestions(rng, nn);
    } else if (kind === 'multiples') {
      questions = genMultiplesQuestions(rng, nn);
    } else if (kind === 'longdiv') {
      questions = genLongDivQuestions(rng, nn);
    } else {
      questions = genWordQuestions(rng, nn);
    }
    return {
      kind,
      label: CN_SECTION_LABELS[idx] ?? String(idx + 1),
      title: meta.title,
      instructions: meta.instructions,
      pointsEach: meta.pointsEach,
      questions,
    };
  });

  return {
    id: opts.id ?? `mul-div-${opts.seed}`,
    title: opts.title ?? `乘法與除法考卷 · 第 ${opts.seed} 回`,
    textbook: '康軒',
    scope: '三下 · 乘法與除法(乘除互逆 / 未知數 / 倍數 / 直式除法 / 應用題)',
    sections,
  };
}

/** 一份考卷的總分(Σ pointsEach×題數)。 */
export function examTotalPoints(exam: MulDivExam): number {
  return exam.sections.reduce((sum, s) => sum + s.pointsEach * s.questions.length, 0);
}

// ───────────────────────────── 驗證器 ─────────────────────────────

/**
 * 驗證一份(產生)乘除考卷是否合法。回**錯誤訊息清單**,空陣列 = 合法。
 *
 * 檢查(lockdown 不打地鼠):
 *   - id 唯一、prompt / answer 非空、每大題至少一題。
 *   - **每題 answer == recomputeMulDivAnswer(verify)**(反算一致)。
 *   - 不超綱硬規則:乘法積 ≤ 999、除法被除數 ≤ 999 且除數 2..9、餘數 < 除數、未知數整除。
 *   - 直式除法驗算式含餘數(有餘數時),驗算等式成立。
 */
export function validateMulDivExam(exam: MulDivExam): string[] {
  const errors: string[] = [];
  const seenIds = new Set<string>();

  if (exam.sections.length === 0) errors.push('考卷沒有任何大題');

  for (const section of exam.sections) {
    if (section.questions.length === 0) {
      errors.push(`${section.label}、${section.title}:沒有任何題目`);
    }
    for (const q of section.questions) {
      if (seenIds.has(q.id)) errors.push(`${q.id}: 重複的題 id`);
      seenIds.add(q.id);
      if (!q.prompt || q.prompt.trim() === '') {
        errors.push(`${q.id}: prompt 為空`);
        continue;
      }
      if (!q.answer || q.answer.trim() === '') {
        errors.push(`${q.id}: answer 為空`);
        continue;
      }
      const expected = recomputeMulDivAnswer(q.verify);
      if (expected === null) {
        errors.push(`${q.id}: verify 規格不合法(${q.verify.op})`);
        continue;
      }
      if (expected !== q.answer) {
        errors.push(`${q.id}: 反算「${expected}」≠ answer「${q.answer}」(${q.verify.op})`);
      }
      // 不超綱硬規則(各 op 補強檢查)。
      checkBounds(q, errors);
    }
  }

  return errors;
}

/** 各題型「不超綱」硬規則(積 ≤ 999、除數一位數、餘數 < 除數、驗算等式成立)。 */
function checkBounds(q: MulDivQuestion, errors: string[]): void {
  const v = q.verify;
  if (v.op === 'inverse-group') {
    if (v.a * v.b > 999) errors.push(`${q.id}: 乘除互逆積超綱(${v.a}×${v.b}>999)`);
  } else if (v.op === 'unknown-factor') {
    if (v.product > 999) errors.push(`${q.id}: 未知數積超綱(${v.product}>999)`);
    if (v.product % v.known !== 0) errors.push(`${q.id}: 因數未知不整除`);
  } else if (v.op === 'unknown-dividend') {
    if (v.quotient * v.divisor > 999) errors.push(`${q.id}: 被除數未知超綱(>999)`);
  } else if (v.op === 'unknown-divisor') {
    if (v.dividend > 999) errors.push(`${q.id}: 除數未知被除數超綱(>999)`);
    if (v.dividend % v.quotient !== 0) errors.push(`${q.id}: 除數未知不整除`);
  } else if (v.op === 'times-divide') {
    if (v.value % v.n !== 0) errors.push(`${q.id}: ÷N 倍器不整除(${v.value}÷${v.n})`);
  } else if (v.op === 'longdiv' || v.op === 'longdiv-check') {
    if (v.divisor < 2 || v.divisor > 9) errors.push(`${q.id}: 直式除法除數非一位數(${v.divisor})`);
    if (v.dividend > 999) errors.push(`${q.id}: 直式除法被除數超綱(${v.dividend}>999)`);
    // 驗算等式成立(商×除數+餘=被除數,餘 < 除數)。
    const qo = Math.floor(v.dividend / v.divisor);
    const r = v.dividend % v.divisor;
    if (qo * v.divisor + r !== v.dividend) errors.push(`${q.id}: 直式除法驗算等式不成立`);
    if (r >= v.divisor) errors.push(`${q.id}: 餘數 ≥ 除數(${r}≥${v.divisor})`);
  } else if (v.op === 'word-divide') {
    if (v.total % v.groups !== 0) errors.push(`${q.id}: 平分不整除(${v.total}÷${v.groups})`);
  } else if (v.op === 'word-ratio') {
    if (v.ask % v.base !== 0) errors.push(`${q.id}: 比例非整數倍(${v.ask}÷${v.base})`);
  } else if (v.op === 'word-remainder') {
    if (v.total % v.per === 0) errors.push(`${q.id}: 有餘數情境卻整除(${v.total}÷${v.per})`);
  }
}
